import { Component, DestroyRef, OnInit, computed, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { RouterOutlet } from '@angular/router'
import { UserService } from './core/services/user.service'
import { VersionCheckService } from './core/version/version-check.service'
import { WhatsNewService } from './core/whats-new/whats-new.service'
import { UpdateModalComponent } from './shared/components/update-modal/update-modal.component'
import { WhatsNewDialogComponent } from './shared/components/whats-new-dialog/whats-new-dialog.component'
import { BasemapApiKeyDialogComponent } from './shared/components/map-layer-control/basemap-api-key-dialog/basemap-api-key-dialog.component'
import { BasemapApiKeyDialogService } from './shared/components/map-layer-control/services/basemap-api-key-dialog.service'
import { ChangelogRelease } from './core/whats-new/changelog'

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, UpdateModalComponent, WhatsNewDialogComponent, BasemapApiKeyDialogComponent],
  template: `
    <router-outlet />

    <!-- App-global overlays. Dialogs share one z-index, so the one later in the
         document paints on top and is the topmost for focus, Tab and Escape
         (ModalShellComponent): the blocking update prompt comes last, over the
         (dismissible) "What's New" dialog and the basemap key dialog. -->
    @if (whatsNew.dialogMode() !== 'hidden') {
      <app-whats-new-dialog
        [releases]="whatsNewReleases()"
        (dismissed)="whatsNew.dismiss()" />
    }

    @if (keyDialog.isOpen()) {
      <app-basemap-api-key-dialog />
    }

    @if (versionCheck.updateAvailable()) {
      <app-update-modal />
    }
  `,
  styles: [`
    /* dvh = the viewport actually visible on mobile (vh ignores the browser's
       address bar, making every page scrollable); vh is the fallback. */
    :host { display: block; height: 100vh; height: 100dvh; }
  `]
})
export class AppComponent implements OnInit {
  private readonly userService = inject(UserService)
  readonly versionCheck = inject(VersionCheckService)
  readonly whatsNew = inject(WhatsNewService)
  readonly keyDialog = inject(BasemapApiKeyDialogService)
  // Captured in a field initializer (an injection context) so it can be handed
  // to takeUntilDestroyed() from ngOnInit, which is not an injection context.
  private readonly destroyRef = inject(DestroyRef)

  /** Whether we've already evaluated the auto-show for this session's user. */
  private autoShowEvaluated = false

  /**
   * The releases to render, derived from the current dialog mode:
   *   • 'auto' — only what changed since the user last dismissed the panel,
   *   • 'full' — everything relevant to the user's roles.
   */
  readonly whatsNewReleases = computed<ChangelogRelease[]>(() => {
    const mode = this.whatsNew.dialogMode()
    const roles = this.userService.currentUser?.roles ?? []
    if (mode === 'hidden') return []
    // 'auto' shows only what changed since the user last dismissed the panel;
    // 'full' shows everything relevant to their roles.
    return mode === 'auto'
      ? this.whatsNew.relevantReleases(roles, this.whatsNew.lastSeenVersion())
      : this.whatsNew.relevantReleases(roles)
  })

  ngOnInit(): void {
    // Begin polling for newer deployed builds.
    this.versionCheck.start()

    // Auto-show the changelog on first sign-in after an upgrade. We react to the
    // first non-null authenticated user; anonymous/logged-out states never
    // trigger it.
    this.userService.currentUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        if (!user || this.autoShowEvaluated) return
        this.autoShowEvaluated = true

        // The update prompt wins — don't stack a changelog behind a mandatory
        // refresh.
        if (this.versionCheck.updateAvailable()) return

        this.whatsNew.maybeAutoShow(user)
      })
  }
}
